import { useEffect, useRef, useState } from "react";
import { translationApi } from "@services/translationApi";
import { isTerminalStatus } from "@models/translation";
import type { GetTranslationResponse } from "@models/translation";

const POLL_INTERVAL_MS = 4000;
const MAX_POLL_MS = 5 * 60 * 1000;

interface TranslationJobState {
  response: GetTranslationResponse | null;
  polling: boolean;
  error: string | null;
  timedOut: boolean;
}

export const useTranslationJob = (
  translationId: string | null,
): TranslationJobState => {
  const [response, setResponse] = useState<GetTranslationResponse | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const startedAt = useRef<number>(0);

  useEffect(() => {
    if (!translationId) {
      setResponse(null);
      setPolling(false);
      setError(null);
      setTimedOut(false);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    startedAt.current = Date.now();
    setPolling(true);
    setError(null);
    setTimedOut(false);
    setResponse(null);

    const tick = async (): Promise<void> => {
      try {
        const data = await translationApi.getTranslation(translationId);
        if (cancelled) return;
        setResponse(data);

        const terminal =
          isTerminalStatus(data.global_status) && data.translations != null;
        const isTimedOut = Date.now() - startedAt.current > MAX_POLL_MS;

        if (terminal || isTimedOut) {
          setPolling(false);
          // Distinguish "gave up waiting" from a real terminal state so the UI
          // can tell the user the job may still be running server-side.
          if (isTimedOut && !terminal) setTimedOut(true);
          return;
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load translation");
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [translationId]);

  return { response, polling, error, timedOut };
};
